import React from "react";
import styled, { keyframes } from "styled-components";

import {
  ADMIN_BUTTON_DEFAULTS,
  ADMIN_BUTTON_ICON_POSITIONS,
  ADMIN_BUTTON_ICON_POSITION_VALUES,
  ADMIN_BUTTON_SIZE_VALUES,
  ADMIN_BUTTON_TYPE_VALUES,
  ADMIN_BUTTON_VARIANT_VALUES,
} from "./adminButtonContract";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const SIZE_STYLES = Object.freeze({
  small: Object.freeze({
    minHeight: "36px",
    padding: "0.5rem 0.75rem",
    fontSize: "0.78rem",
    borderRadius: "9px",
    gap: "0.4rem",
    iconSize: "0.9rem",
  }),
  medium: Object.freeze({
    minHeight: "40px",
    padding: "0.65rem 1rem",
    fontSize: "0.86rem",
    borderRadius: "10px",
    gap: "0.5rem",
    iconSize: "1rem",
  }),
  large: Object.freeze({
    minHeight: "46px",
    padding: "0.85rem 1.15rem",
    fontSize: "0.94rem",
    borderRadius: "12px",
    gap: "0.55rem",
    iconSize: "1.1rem",
  }),
});

const VARIANT_STYLES = Object.freeze({
  primary: Object.freeze({
    background: "var(--q360-button-primary-bg, #fff9ea)",
    color: "var(--q360-button-primary-text, #0b1328)",
    border: "1px solid transparent",
    hoverBackground: "var(--q360-button-primary-hover-bg, #ffffff)",
    activeBackground: "var(--q360-button-primary-active-bg, #f2ead5)",
  }),
  secondary: Object.freeze({
    background: "var(--q360-button-secondary-bg, rgba(255, 255, 255, 0.1))",
    color: "var(--q360-button-secondary-text, #fff9ea)",
    border:
      "1px solid var(--q360-button-secondary-border, rgba(255, 249, 234, 0.35))",
    hoverBackground:
      "var(--q360-button-secondary-hover-bg, rgba(255, 255, 255, 0.16))",
    activeBackground:
      "var(--q360-button-secondary-active-bg, rgba(255, 255, 255, 0.08))",
  }),
  ghost: Object.freeze({
    background: "transparent",
    color: "var(--q360-button-ghost-text, #fff9ea)",
    border:
      "1px solid var(--q360-button-ghost-border, rgba(255, 249, 234, 0.24))",
    hoverBackground:
      "var(--q360-button-ghost-hover-bg, rgba(255, 255, 255, 0.08))",
    activeBackground:
      "var(--q360-button-ghost-active-bg, rgba(255, 255, 255, 0.04))",
  }),
  success: Object.freeze({
    background: "var(--q360-button-success-bg, #d7ffd9)",
    color: "var(--q360-button-success-text, #0b1328)",
    border: "1px solid transparent",
    hoverBackground: "var(--q360-button-success-hover-bg, #e5ffe7)",
    activeBackground: "var(--q360-button-success-active-bg, #bfeec2)",
  }),
  danger: Object.freeze({
    background: "var(--q360-button-danger-bg, rgba(180, 40, 40, 0.92))",
    color: "var(--q360-button-danger-text, #ffffff)",
    border: "1px solid transparent",
    hoverBackground: "var(--q360-button-danger-hover-bg, #c53030)",
    activeBackground: "var(--q360-button-danger-active-bg, #991b1b)",
  }),
});

const resolveSize = (size) =>
  ADMIN_BUTTON_SIZE_VALUES.includes(size)
    ? size
    : ADMIN_BUTTON_DEFAULTS.SIZE;

const resolveVariant = (variant) =>
  ADMIN_BUTTON_VARIANT_VALUES.includes(variant)
    ? variant
    : ADMIN_BUTTON_DEFAULTS.VARIANT;

const resolveIconPosition = (iconPosition) =>
  ADMIN_BUTTON_ICON_POSITION_VALUES.includes(iconPosition)
    ? iconPosition
    : ADMIN_BUTTON_DEFAULTS.ICON_POSITION;

const resolveType = (type) =>
  ADMIN_BUTTON_TYPE_VALUES.includes(type)
    ? type
    : ADMIN_BUTTON_DEFAULTS.TYPE;

const StyledButton = styled.button`
  ${({ $size }) => {
    const size = SIZE_STYLES[$size];

    return `
      min-height: ${size.minHeight};
      padding: ${size.padding};
      border-radius: ${size.borderRadius};
      gap: ${size.gap};
      font-size: ${size.fontSize};
    `;
  }}

  ${({ $variant }) => {
    const variant = VARIANT_STYLES[$variant];

    return `
      background: ${variant.background};
      color: ${variant.color};
      border: ${variant.border};

      &:not(:disabled):hover {
        background: ${variant.hoverBackground};
      }

      &:not(:disabled):active {
        background: ${variant.activeBackground};
        transform: translateY(1px);
      }
    `;
  }}

  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-family: inherit;
  font-weight: 850;
  line-height: 1.1;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  appearance: none;
  transition:
    background 140ms ease,
    border-color 140ms ease,
    color 140ms ease,
    opacity 140ms ease,
    transform 80ms ease,
    box-shadow 140ms ease;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--q360-button-focus-ring, #ffffff);
    outline-offset: 3px;
    box-shadow: 0 0 0 2px var(--q360-button-focus-shadow, #0b1328);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
  }
`;

const IconSlot = styled.span`
  width: ${({ $size }) => SIZE_STYLES[$size].iconSize};
  height: ${({ $size }) => SIZE_STYLES[$size].iconSize};
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  line-height: 1;

  > svg,
  > img {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const Spinner = styled.span`
  width: ${({ $size }) => SIZE_STYLES[$size].iconSize};
  height: ${({ $size }) => SIZE_STYLES[$size].iconSize};
  display: inline-block;
  flex: 0 0 auto;
  box-sizing: border-box;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 999px;
  animation: ${spin} 650ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1.5s;
  }
`;

const Content = styled.span`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const renderIcon = (icon, size) => {
  if (!icon) {
    return null;
  }

  return (
    <IconSlot $size={size} aria-hidden="true">
      {icon}
    </IconSlot>
  );
};

const AdminButton = React.forwardRef(function AdminButton(
  {
    children,
    variant = ADMIN_BUTTON_DEFAULTS.VARIANT,
    size = ADMIN_BUTTON_DEFAULTS.SIZE,
    type = ADMIN_BUTTON_DEFAULTS.TYPE,
    icon = null,
    iconPosition = ADMIN_BUTTON_DEFAULTS.ICON_POSITION,
    loading = false,
    disabled = false,
    fullWidth = false,
    onClick,
    "aria-label": ariaLabel,
    ...buttonProps
  },
  ref,
) {
  const resolvedVariant = resolveVariant(variant);
  const resolvedSize = resolveSize(size);
  const resolvedIconPosition = resolveIconPosition(iconPosition);
  const resolvedType = resolveType(type);
  const isDisabled = Boolean(disabled || loading);

  const handleClick = (event) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  const leadingContent = loading
    ? <Spinner $size={resolvedSize} aria-hidden="true" />
    : resolvedIconPosition === ADMIN_BUTTON_ICON_POSITIONS.START
      ? renderIcon(icon, resolvedSize)
      : null;

  const trailingContent =
    !loading &&
    resolvedIconPosition === ADMIN_BUTTON_ICON_POSITIONS.END
      ? renderIcon(icon, resolvedSize)
      : null;

  return (
    <StyledButton
      {...buttonProps}
      ref={ref}
      type={resolvedType}
      disabled={isDisabled}
      aria-disabled={isDisabled ? "true" : undefined}
      aria-busy={loading ? "true" : undefined}
      aria-label={ariaLabel}
      onClick={handleClick}
      $variant={resolvedVariant}
      $size={resolvedSize}
      $fullWidth={Boolean(fullWidth)}
      data-admin-button=""
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-loading={loading ? "true" : "false"}
    >
      {leadingContent}
      {children !== undefined && children !== null ? (
        <Content>{children}</Content>
      ) : null}
      {trailingContent}
    </StyledButton>
  );
});

AdminButton.displayName = "AdminButton";

export default AdminButton;
